/**
 * Structured Data (JSON-LD) generators for rich snippets
 * Helps search engines understand content and display rich results
 */

import { seoConfig } from '../config/seo'

/**
 * Generate Organization structured data
 */
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: seoConfig.organization.name,
    url: seoConfig.organization.url,
    logo: seoConfig.organization.logo,
    description: seoConfig.organization.description,
    foundingDate: seoConfig.organization.foundingDate,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: seoConfig.organization.contactPoint.contactType,
      email: seoConfig.organization.contactPoint.email,
    },
    sameAs: [
      `https://twitter.com/${seoConfig.social.twitter}`,
      `https://facebook.com/${seoConfig.social.facebook}`,
      `https://linkedin.com/${seoConfig.social.linkedin}`,
    ],
  }
}

/**
 * Generate Website structured data
 */
export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: seoConfig.siteName,
    url: seoConfig.siteUrl,
    description: seoConfig.defaultDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${seoConfig.siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

/**
 * Generate Product structured data
 */
export function generateProductSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: seoConfig.product.name,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: seoConfig.product.description,
    offers: [
      {
        '@type': 'Offer',
        name: seoConfig.product.offers.beta.name,
        price: seoConfig.product.offers.beta.price,
        priceCurrency: seoConfig.product.offers.beta.priceCurrency,
        priceValidUntil: seoConfig.product.offers.beta.priceValidUntil,
        description: seoConfig.product.offers.beta.description,
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: seoConfig.product.offers.core.name,
        price: seoConfig.product.offers.core.price,
        priceCurrency: seoConfig.product.offers.core.priceCurrency,
        priceValidUntil: seoConfig.product.offers.core.priceValidUntil,
        description: seoConfig.product.offers.core.description,
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: seoConfig.product.offers.scale.name,
        price: seoConfig.product.offers.scale.price,
        priceCurrency: seoConfig.product.offers.scale.priceCurrency,
        priceValidUntil: seoConfig.product.offers.scale.priceValidUntil,
        description: seoConfig.product.offers.scale.description,
        availability: 'https://schema.org/InStock',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '200',
      bestRating: '5',
      worstRating: '1',
    },
  }
}

/**
 * Generate FAQ structured data
 */
export function generateFAQSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: seoConfig.faqSchema.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }
}

/**
 * Generate Breadcrumb structured data
 */
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

/**
 * Generate Review structured data
 */
export function generateReviewSchema(
  reviews: Array<{
    author: string
    rating: number
    reviewBody: string
    datePublished: string
  }>,
) {
  return reviews.map((review) => ({
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.author,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: '5',
      worstRating: '1',
    },
    reviewBody: review.reviewBody,
    datePublished: review.datePublished,
    itemReviewed: {
      '@type': 'SoftwareApplication',
      name: seoConfig.product.name,
    },
  }))
}

/**
 * Generate combined structured data for homepage
 */
export function generateHomepageSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      generateOrganizationSchema(),
      generateWebsiteSchema(),
      generateProductSchema(),
    ],
  }
}
